import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  Trophy, Plus, Trash2, Save, Clock, DollarSign, FileText,
  ChevronDown, ChevronUp, Copy, PlayCircle, Gamepad2, Zap, Crown, Users,
  Gift, Key, Package, Bell, Eye, EyeOff, GripVertical, Lock, Cloud, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { GameSearch } from './GameSearch';

const PRIZE_TYPES = [
  { value: 'money', label: 'Cash', icon: DollarSign, color: 'text-green-400' },
  { value: 'game_code', label: 'Game Code', icon: Key, color: 'text-blue-400' },
  { value: 'item', label: 'Item/Prize', icon: Gift, color: 'text-purple-400' }
];

// Rule visibility options
const RULE_VISIBILITY = {
  ALWAYS: 'always',        // Show on both scheduled and live
  LIVE_ONLY: 'live_only',  // Only show when challenge is live
  BLUR_SCHEDULED: 'blur_scheduled'  // Show blurred on scheduled, clear on live
};

// Default API URL fallback
const DEFAULT_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default datetime converter
const defaultLocalDatetimeToIso = (localDatetime) => {
  if (!localDatetime) return null;
  try {
    const date = new Date(localDatetime);
    if (isNaN(date.getTime())) return null;
    return date.toISOString();
  } catch (e) {
    return null;
  }
};

// Helper functions moved outside component
function createEmptyPrizeConfig(place) {
  return {
    prize_type: 'money',
    amount: place === 1 ? 100 : place === 2 ? 50 : 25,
    description: '',
    code: '',
    item_details: ''
  };
}

function createEmptyChallenge() {
  return {
    id: Date.now(),
    title: '',
    rules: '',
    rules_config: [], // New: Array of {text, visibility} objects
    prize_amount: 100,
    prize_1st: 100,
    prize_2nd: 50,
    prize_3rd: 25,
    prize_currency: 'USD',
    prize_1st_config: createEmptyPrizeConfig(1),
    prize_2nd_config: createEmptyPrizeConfig(2),
    prize_3rd_config: createEmptyPrizeConfig(3),
    bounties: [], // New: Array of {prize, rule} objects
    start_time: '',
    end_time: '',
    max_winners: 1,
    game: null,
    useDefaultGame: true,
    notify_discord: true,
    notify_email: true
  };
}

const ChallengeBuilder = ({ 
  token, 
  API = DEFAULT_API, 
  challenges = [], 
  setChallenges, 
  settings,
  refreshData,
  localDatetimeToIso = defaultLocalDatetimeToIso,
  initialData = null,
  loadedDraft = null
}) => {
  // Create initial challenge from initialData (for cloning) or empty
  const getInitialQueue = () => {
    if (loadedDraft) {
      // Loading an existing draft
      let rulesConfig = loadedDraft.rules_config || [];
      if (rulesConfig.length === 0 && loadedDraft.rules) {
        rulesConfig = loadedDraft.rules.split('\n').filter(r => r.trim()).map(text => ({
          text: text.trim(),
          visibility: RULE_VISIBILITY.ALWAYS
        }));
      }
      
      return [{
        id: Date.now(),
        draftId: loadedDraft.id,
        title: loadedDraft.title || '',
        rules: loadedDraft.rules || '',
        rules_config: rulesConfig,
        prize_amount: loadedDraft.prize_amount || 100,
        prize_1st: loadedDraft.prize_1st || loadedDraft.prize_amount || 100,
        prize_2nd: loadedDraft.prize_2nd || 50,
        prize_3rd: loadedDraft.prize_3rd || 25,
        prize_currency: loadedDraft.prize_currency || 'USD',
        prize_1st_config: loadedDraft.prize_1st_config || createEmptyPrizeConfig(1),
        prize_2nd_config: loadedDraft.prize_2nd_config || createEmptyPrizeConfig(2),
        prize_3rd_config: loadedDraft.prize_3rd_config || createEmptyPrizeConfig(3),
        bounties: loadedDraft.bounties || [],
        start_time: '',
        end_time: '',
        max_winners: loadedDraft.max_winners || 1,
        game: loadedDraft.game_name ? {
          id: loadedDraft.game_id,
          name: loadedDraft.game_name,
          cover_url: loadedDraft.game_cover_url
        } : null,
        useDefaultGame: false
      }];
    }
    
    if (initialData) {
      // Parse rules_config from initialData or convert rules string to config
      let rulesConfig = initialData.rules_config || [];
      if (rulesConfig.length === 0 && initialData.rules) {
        // Convert legacy rules string to config format
        rulesConfig = initialData.rules.split('\n').filter(r => r.trim()).map(text => ({
          text: text.trim(),
          visibility: RULE_VISIBILITY.ALWAYS
        }));
      }
      
      return [{
        id: Date.now(),
        title: initialData.title || '',
        rules: initialData.rules || '',
        rules_config: rulesConfig,
        prize_amount: initialData.prize_amount || 100,
        prize_1st: initialData.prize_1st || initialData.prize_amount || 100,
        prize_2nd: initialData.prize_2nd || 50,
        prize_3rd: initialData.prize_3rd || 25,
        prize_currency: initialData.prize_currency || 'USD',
        prize_1st_config: initialData.prize_1st_config || createEmptyPrizeConfig(1),
        prize_2nd_config: initialData.prize_2nd_config || createEmptyPrizeConfig(2),
        prize_3rd_config: initialData.prize_3rd_config || createEmptyPrizeConfig(3),
        bounties: initialData.bounties || [],
        start_time: '',
        end_time: '',
        max_winners: initialData.max_winners || 1,
        game: initialData.game_name ? {
          id: initialData.game_id,
          name: initialData.game_name,
          cover_url: initialData.game_cover_url
        } : null,
        useDefaultGame: false
      }];
    }
    return [createEmptyChallenge()];
  };

  // Challenge queue - unified for both single and batch
  const [challengeQueue, setChallengeQueue] = useState(getInitialQueue);
  const [expandedIndex, setExpandedIndex] = useState(0);
  const [isCreating, setIsCreating] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // idle, saving, saved, error

  // Default game for all challenges in queue (optional)
  const [defaultGame, setDefaultGame] = useState(null);

  // Auto-save functionality
  const autoSaveTimerRef = useRef(null);
  const lastSavedStateRef = useRef(null);
  const isUnmountingRef = useRef(false);
  const activeDraftIdRef = useRef(loadedDraft?.id || initialData?.draftId || null);
  const isSavingRef = useRef(false);

  // Sync activeDraftIdRef when loadedDraft changes
  useEffect(() => {
    if (loadedDraft?.id) {
      activeDraftIdRef.current = loadedDraft.id;
    }
  }, [loadedDraft]);

  // Function to perform the save (can be called synchronously or async)
  const performSave = async () => {
    if (isSavingRef.current || isUnmountingRef.current) return false;

    const challenge = challengeQueue[0];
    if (!challenge) return false;
    
    const currentDraftId = challenge.draftId || activeDraftIdRef.current;

    // Don't auto-save if completely empty
    if (!challenge.title && !challenge.rules && challenge.prize_amount === 100 && !currentDraftId) {
      return false;
    }

    isSavingRef.current = true;

    try {
      const axios = (await import('axios')).default;
      const gameToUse = challenge.useDefaultGame ? defaultGame : challenge.game;
      
      const buildPrizeConfig = (config) => {
        if (!config) return null;
        return {
          prize_type: config.prize_type || 'money',
          amount: config.prize_type === 'money' ? (parseFloat(config.amount) || 0) : null,
          description: config.description || null,
          code: config.prize_type === 'game_code' ? config.code : null,
          item_details: config.prize_type === 'item' ? config.item_details : null
        };
      };

      const payload = {
        title: challenge.title || 'Untitled Draft',
        rules: challenge.rules,
        rules_config: challenge.rules_config || [],
        prize_amount: challenge.prize_1st_config?.amount || challenge.prize_1st || challenge.prize_amount,
        prize_1st: challenge.prize_1st_config?.amount || challenge.prize_1st,
        prize_2nd: challenge.max_winners >= 2 ? (challenge.prize_2nd_config?.amount || challenge.prize_2nd) : null,
        prize_3rd: challenge.max_winners >= 3 ? (challenge.prize_3rd_config?.amount || challenge.prize_3rd) : null,
        prize_currency: challenge.prize_currency,
        prize_1st_config: buildPrizeConfig(challenge.prize_1st_config),
        prize_2nd_config: challenge.max_winners >= 2 ? buildPrizeConfig(challenge.prize_2nd_config) : null,
        prize_3rd_config: challenge.max_winners >= 3 ? buildPrizeConfig(challenge.prize_3rd_config) : null,
        bounties: challenge.bounties || [],
        start_time: challenge.start_time ? localDatetimeToIso(challenge.start_time) : null,
        end_time: challenge.end_time ? localDatetimeToIso(challenge.end_time) : null,
        max_winners: challenge.max_winners || 1,
        game_id: gameToUse?.id || null,
        game_name: gameToUse?.name || null,
        game_cover_url: gameToUse?.cover_url || null
      };

      let response;
      let newDraftId = currentDraftId;

      if (currentDraftId) {
        // Update existing draft
        response = await axios.put(`${API}/admin/challenges/draft/${currentDraftId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Create new draft ONCE
        response = await axios.post(`${API}/admin/challenges/draft`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        newDraftId = response.data?.id;
        if (newDraftId) {
          activeDraftIdRef.current = newDraftId;
          setChallengeQueue(prev => prev.map((c, i) => 
            i === 0 ? { ...c, draftId: newDraftId } : c
          ));
        }
      }

      // Update last saved state reference including draft ID
      const savedState = challengeQueue.map((c, i) => i === 0 ? { ...c, draftId: newDraftId } : c);
      lastSavedStateRef.current = JSON.stringify(savedState);

      if (!isUnmountingRef.current) {
        setLastSaved(new Date());
        setAutoSaveStatus('saved');
        
        setTimeout(() => {
          if (!isUnmountingRef.current) {
            setAutoSaveStatus('idle');
          }
        }, 2000);
      }
      
      return true;
    } catch (error) {
      console.error('Save draft failed:', error);
      if (!isUnmountingRef.current) {
        setAutoSaveStatus('error');
        setTimeout(() => {
          if (!isUnmountingRef.current) {
            setAutoSaveStatus('idle');
          }
        }, 3000);
      }
      return false;
    } finally {
      isSavingRef.current = false;
    }
  };

  const handleSaveDraftManual = async () => {
    setIsSavingDraft(true);
    setAutoSaveStatus('saving');
    const success = await performSave();
    setIsSavingDraft(false);
    if (success) {
      toast.success('💾 Draft saved successfully!');
      if (refreshData) {
        await refreshData();
      }
    } else {
      toast.error('Failed to save draft');
    }
  };

  const addChallenge = () => {
    setChallengeQueue([...challengeQueue, createEmptyChallenge()]);
    setExpandedIndex(challengeQueue.length);
  };

  const removeChallenge = (index) => {
    if (challengeQueue.length > 1) {
      const newQueue = challengeQueue.filter((_, i) => i !== index);
      setChallengeQueue(newQueue);
      if (expandedIndex >= index && expandedIndex > 0) {
        setExpandedIndex(expandedIndex - 1);
      }
    }
  };

  const duplicateChallenge = (index) => {
    const challenge = challengeQueue[index];
    const duplicate = {
      ...challenge,
      id: Date.now(),
      title: `${challenge.title} (Copy)`
    };
    const newQueue = [...challengeQueue];
    newQueue.splice(index + 1, 0, duplicate);
    setChallengeQueue(newQueue);
    setExpandedIndex(index + 1);
  };

  const updateChallenge = (index, field, value) => {
    setChallengeQueue(challengeQueue.map((c, i) => 
      i === index ? { ...c, [field]: value } : c
    ));
  };

  const updatePrizeConfig = (index, place, field, value) => {
    const configKey = `prize_${place}${place === 1 ? 'st' : place === 2 ? 'nd' : 'rd'}_config`;
    setChallengeQueue(challengeQueue.map((c, i) => {
      if (i !== index) return c;
      return {
        ...c,
        [configKey]: {
          ...c[configKey],
          [field]: value
        }
      };
    }));
  };

  const handleCreateAll = async () => {
    const validChallenges = challengeQueue.filter(c => c.title.trim());
    if (validChallenges.length === 0) {
      toast.error('Add at least one challenge with a title');
      return;
    }

    setIsCreating(true);
    try {
      const createdChallenges = [];
      const axios = (await import('axios')).default;

      for (const challenge of validChallenges) {
        const gameToUse = challenge.useDefaultGame ? defaultGame : challenge.game;
        
        // Build prize configs for each place
        const buildPrizeConfig = (config) => {
          if (!config) return null;
          return {
            prize_type: config.prize_type || 'money',
            amount: config.prize_type === 'money' ? (parseFloat(config.amount) || 0) : null,
            description: config.description || null,
            code: config.prize_type === 'game_code' ? config.code : null,
            item_details: config.prize_type === 'item' ? config.item_details : null
          };
        };
        
        const response = await axios.post(`${API}/admin/challenges`, {
          title: challenge.title,
          rules: challenge.rules,
          rules_config: challenge.rules_config || [],
          prize_amount: challenge.prize_1st_config?.amount || challenge.prize_1st || challenge.prize_amount,
          prize_1st: challenge.prize_1st_config?.amount || challenge.prize_1st,
          prize_2nd: challenge.max_winners >= 2 ? (challenge.prize_2nd_config?.amount || challenge.prize_2nd) : null,
          prize_3rd: challenge.max_winners >= 3 ? (challenge.prize_3rd_config?.amount || challenge.prize_3rd) : null,
          prize_currency: challenge.prize_currency,
          prize_1st_config: buildPrizeConfig(challenge.prize_1st_config),
          prize_2nd_config: challenge.max_winners >= 2 ? buildPrizeConfig(challenge.prize_2nd_config) : null,
          prize_3rd_config: challenge.max_winners >= 3 ? buildPrizeConfig(challenge.prize_3rd_config) : null,
          bounties: challenge.bounties || [],
          start_time: challenge.start_time ? localDatetimeToIso(challenge.start_time) : null,
          end_time: challenge.end_time ? localDatetimeToIso(challenge.end_time) : null,
          max_winners: challenge.max_winners || 1,
          game_id: gameToUse?.id || null,
          game_name: gameToUse?.name || null,
          game_cover_url: gameToUse?.cover_url || null,
          notify_discord: challenge.notify_discord !== false,
          notify_email: challenge.notify_email !== false
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        createdChallenges.push(response.data);
      }

      // Update local state if setChallenges is provided
      if (setChallenges && challenges) {
        setChallenges([...createdChallenges, ...challenges]);
      }
      
      // Reset queue
      setChallengeQueue([createEmptyChallenge()]);
      setExpandedIndex(0);
      setDefaultGame(null);
      
      toast.success(`🎉 Created ${createdChallenges.length} challenge${createdChallenges.length > 1 ? 's' : ''}`);
      
      if (refreshData) {
        await refreshData();
      }
    } catch (error) {
      console.error('Failed to create challenges:', error);
      toast.error(error.response?.data?.detail || 'Failed to create challenges');
    } finally {
      setIsCreating(false);
    }
  };

  const validCount = challengeQueue.filter(c => c.title.trim()).length;
  const totalPrize = challengeQueue.reduce((sum, c) => {
    const p1 = parseFloat(c.prize_1st) || 0;
    const p2 = c.max_winners >= 2 ? (parseFloat(c.prize_2nd) || 0) : 0;
    const p3 = c.max_winners >= 3 ? (parseFloat(c.prize_3rd) || 0) : 0;
    return sum + p1 + p2 + p3;
  }, 0);

  return (
    <Card className="glass-card border-red-900/30">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <CardTitle className="flex items-center gap-2 text-sm sm:text-base" style={{ fontFamily: 'Orbitron, sans-serif' }}>
              <Trophy className="w-5 h-5 text-yellow-400" />
              Challenge Builder
            </CardTitle>
            
            {/* Auto-save indicator */}
            {autoSaveStatus !== 'idle' && (
              <div className="flex items-center gap-1.5 text-xs">
                {autoSaveStatus === 'saving' && (
                  <>
                    <Cloud className="w-3.5 h-3.5 text-blue-400 animate-pulse" />
                    <span className="text-blue-400">Saving...</span>
                  </>
                )}
                {autoSaveStatus === 'saved' && (
                  <>
                    <CheckCircle className="w-3.5 h-3.5 text-green-400" />
                    <span className="text-green-400">Saved</span>
                  </>
                )}
                {autoSaveStatus === 'error' && (
                  <>
                    <span className="text-red-400">⚠ Save failed</span>
                  </>
                )}
              </div>
            )}
            {lastSaved && autoSaveStatus === 'idle' && (
              <span className="text-xs text-gray-500">
                Last saved: {lastSaved.toLocaleTimeString()}
              </span>
            )}
          </div>
          
          {/* Summary Badge */}
          <div className="flex items-center gap-2 text-xs">
            <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded font-bold">
              {validCount} Challenge{validCount !== 1 ? 's' : ''}
            </span>
            <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded font-bold">
              ${totalPrize} Total
            </span>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Default Game Selector - Applies to all challenges */}
        <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Gamepad2 className="w-4 h-4 text-purple-400" />
            <span className="text-xs text-purple-400 font-bold uppercase">Default Game (applies to all)</span>
          </div>
          <GameSearch 
            selectedGame={defaultGame} 
            onSelectGame={setDefaultGame}
            compact
          />
        </div>

        {/* Challenge Queue */}
        <div className="space-y-2">
          {challengeQueue.map((challenge, index) => (
            <div 
              key={challenge.id}
              className={`border rounded-lg transition-all ${
                expandedIndex === index 
                  ? 'border-yellow-500/50 bg-yellow-500/5' 
                  : 'border-red-900/30 bg-black/30'
              }`}
            >
              {/* Challenge Header - Always Visible */}
              <div 
                className="flex items-center justify-between p-3 cursor-pointer"
                onClick={() => setExpandedIndex(expandedIndex === index ? -1 : index)}
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                    challenge.title.trim() ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-500'
                  }`}>
                    {index + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className={`font-medium text-sm truncate ${!challenge.title.trim() && 'text-gray-500 italic'}`}>
                      {challenge.title.trim() || 'Untitled Challenge'}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span className="text-green-400">${challenge.prize_amount}</span>
                      {(challenge.useDefaultGame && defaultGame) || challenge.game ? (
                        <span className="text-purple-400">
                          {challenge.useDefaultGame && defaultGame ? defaultGame.name : challenge.game?.name}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-gray-400 hover:text-blue-400"
                    onClick={(e) => { e.stopPropagation(); duplicateChallenge(index); }}
                    title="Duplicate"
                  >
                    <Copy className="w-3 h-3" />
                  </Button>
                  {challengeQueue.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-gray-400 hover:text-red-400"
                      onClick={(e) => { e.stopPropagation(); removeChallenge(index); }}
                      title="Remove"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                  {expandedIndex === index ? (
                    <ChevronUp className="w-4 h-4 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  )}
                </div>
              </div>

              {/* Challenge Details - Expanded */}
              {expandedIndex === index && (
                <div className="px-3 pb-3 space-y-3 border-t border-red-900/20 pt-3">
                  {/* Title and Prize Row */}
                  <div className="grid sm:grid-cols-4 gap-2">
                    <div className="sm:col-span-2 space-y-1">
                      <Label className="text-xs text-gray-400">Title *</Label>
                      <Input
                        value={challenge.title}
                        onChange={(e) => updateChallenge(index, 'title', e.target.value)}
                        className="bg-black/50 border-red-900/30"
                        placeholder="Challenge title..."
                        data-testid={`challenge-${index}-title`}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-400">Currency</Label>
                      <Input
                        type="text"
                        value={challenge.prize_currency}
                        onChange={(e) => updateChallenge(index, 'prize_currency', e.target.value)}
                        className="bg-black/50 border-red-900/30"
                        placeholder="USD"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-yellow-400 flex items-center gap-1">
                        <Crown className="w-3 h-3" /> Winners
                      </Label>
                      <div className="flex gap-1">
                        {[1, 2, 3].map((num) => (
                          <Button
                            key={num}
                            type="button"
                            size="sm"
                            variant={challenge.max_winners === num ? "default" : "outline"}
                            className={`flex-1 h-9 ${
                              challenge.max_winners === num 
                                ? 'bg-yellow-500 hover:bg-yellow-600 text-black' 
                                : 'border-gray-700 text-gray-400 hover:bg-gray-800'
                            }`}
                            onClick={() => updateChallenge(index, 'max_winners', num)}
                            data-testid={`challenge-${index}-winners-${num}`}
                          >
                            {num === 1 ? '🥇' : num === 2 ? '🥇🥈' : '🥇🥈🥉'}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Prize Configuration per Place */}
                  <div className="p-3 bg-gradient-to-br from-green-500/10 to-purple-500/10 border border-green-500/30 rounded-lg space-y-4">
                    <Label className="text-xs text-green-400 mb-2 block font-bold">🎁 Prize Configuration</Label>
                    
                    {/* 1st Place Prize */}
                    <PrizeConfigRow
                      place={1}
                      config={challenge.prize_1st_config}
                      currency={challenge.prize_currency}
                      onUpdate={(field, value) => updatePrizeConfig(index, 1, field, value)}
                    />
                    
                    {/* 2nd Place Prize */}
                    {challenge.max_winners >= 2 && (
                      <PrizeConfigRow
                        place={2}
                        config={challenge.prize_2nd_config}
                        currency={challenge.prize_currency}
                        onUpdate={(field, value) => updatePrizeConfig(index, 2, field, value)}
                      />
                    )}
                    
                    {/* 3rd Place Prize */}
                    {challenge.max_winners >= 3 && (
                      <PrizeConfigRow
                        place={3}
                        config={challenge.prize_3rd_config}
                        currency={challenge.prize_currency}
                        onUpdate={(field, value) => updatePrizeConfig(index, 3, field, value)}
                      />
                    )}
                    
                    <p className="text-xs text-gray-500 mt-2">
                      💡 Tip: Game codes are revealed only to winners. Item prizes collect shipping address.
                    </p>
                  </div>

                  {/* Rules Editor */}
                  <RulesEditor
                    rulesConfig={challenge.rules_config || []}
                    onChange={(newConfig) => {
                      // Update both rules_config and legacy rules string
                      const rulesString = newConfig.map(r => r.text).join('\n');
                      setChallengeQueue(challengeQueue.map((c, i) => 
                        i === index ? { ...c, rules_config: newConfig, rules: rulesString } : c
                      ));
                    }}
                    testIdPrefix={`challenge-${index}`}
                  />

                  {/* Bounties Section */}
                  <div className="p-3 bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border border-yellow-500/30 rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs text-yellow-400 font-bold flex items-center gap-1">
                        🎁 Bounties (Optional)
                      </Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newBounties = [...(challenge.bounties || []), { prize: '', rule: '' }];
                          setChallengeQueue(challengeQueue.map((c, i) => 
                            i === index ? { ...c, bounties: newBounties } : c
                          ));
                        }}
                        className="h-6 px-2 text-xs text-yellow-400 hover:bg-yellow-500/20"
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Add Bounty
                      </Button>
                    </div>
                    
                    {challenge.bounties && challenge.bounties.length > 0 ? (
                      <div className="space-y-2">
                        {challenge.bounties.map((bounty, bountyIdx) => (
                          <div key={bountyIdx} className="p-2 bg-black/30 border border-yellow-900/30 rounded space-y-2">
                            <div className="flex items-start gap-2">
                              <div className="flex-1 space-y-2">
                                <div>
                                  <Label className="text-xs text-yellow-300 mb-1">Prize</Label>
                                  <Input
                                    placeholder="e.g., Extra $50, Exclusive emote"
                                    value={bounty.prize}
                                    onChange={(e) => {
                                      const newBounties = [...challenge.bounties];
                                      newBounties[bountyIdx] = { ...newBounties[bountyIdx], prize: e.target.value };
                                      setChallengeQueue(challengeQueue.map((c, i) => 
                                        i === index ? { ...c, bounties: newBounties } : c
                                      ));
                                    }}
                                    className="bg-black/50 border-yellow-900/30 text-sm"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs text-orange-300 mb-1">Rule/Condition</Label>
                                  <Input
                                    placeholder="e.g., Complete under 1 hour, No damage taken"
                                    value={bounty.rule}
                                    onChange={(e) => {
                                      const newBounties = [...challenge.bounties];
                                      newBounties[bountyIdx] = { ...newBounties[bountyIdx], rule: e.target.value };
                                      setChallengeQueue(challengeQueue.map((c, i) => 
                                        i === index ? { ...c, bounties: newBounties } : c
                                      ));
                                    }}
                                    className="bg-black/50 border-orange-900/30 text-sm"
                                  />
                                </div>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const newBounties = challenge.bounties.filter((_, idx) => idx !== bountyIdx);
                                  setChallengeQueue(challengeQueue.map((c, i) => 
                                    i === index ? { ...c, bounties: newBounties } : c
                                  ));
                                }}
                                className="h-8 w-8 p-0 text-red-400 hover:bg-red-500/20 mt-5"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500 italic">No bounties added. Click "Add Bounty" to create optional extra prizes.</p>
                    )}
                    
                    <p className="text-xs text-gray-500 mt-2">
                      💡 Bounties are optional extra prizes for completing additional challenge rules.
                    </p>
                  </div>

                  {/* Time Settings */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs text-yellow-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Start Time
                      </Label>
                      <Input
                        type="datetime-local"
                        value={challenge.start_time}
                        onChange={(e) => updateChallenge(index, 'start_time', e.target.value)}
                        className="bg-black/50 border-red-900/30 text-sm"
                        data-testid={`challenge-${index}-start`}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-red-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> End Time
                      </Label>
                      <Input
                        type="datetime-local"
                        value={challenge.end_time}
                        onChange={(e) => updateChallenge(index, 'end_time', e.target.value)}
                        className="bg-black/50 border-red-900/30 text-sm"
                        data-testid={`challenge-${index}-end`}
                      />
                    </div>
                  </div>

                  {/* Individual Game Override */}
                  <div className="p-2 bg-black/30 border border-gray-800 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <input
                        type="checkbox"
                        id={`useDefault-${index}`}
                        checked={challenge.useDefaultGame}
                        onChange={(e) => updateChallenge(index, 'useDefaultGame', e.target.checked)}
                        className="rounded border-gray-600"
                      />
                      <label htmlFor={`useDefault-${index}`} className="text-xs text-gray-400">
                        Use default game {defaultGame ? `(${defaultGame.name})` : '(none selected)'}
                      </label>
                    </div>
                    {!challenge.useDefaultGame && (
                      <GameSearch 
                        selectedGame={challenge.game} 
                        onSelectGame={(game) => updateChallenge(index, 'game', game)}
                        compact
                      />
                    )}
                  </div>

                  {/* Notification Options */}
                  <div className="p-2 bg-black/30 border border-blue-900/30 rounded-lg">
                    <p className="text-xs text-blue-400 font-bold mb-2 flex items-center gap-1">
                      <Bell className="w-3 h-3" /> Push Notifications
                    </p>
                    <div className="flex flex-wrap gap-4">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id={`notify-discord-${index}`}
                          checked={challenge.notify_discord !== false}
                          onChange={(e) => updateChallenge(index, 'notify_discord', e.target.checked)}
                          className="rounded border-gray-600 text-indigo-500 focus:ring-indigo-500"
                        />
                        <label htmlFor={`notify-discord-${index}`} className="text-xs text-gray-400 flex items-center gap-1">
                          <span className="text-indigo-400">Discord</span>
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id={`notify-email-${index}`}
                          checked={challenge.notify_email !== false}
                          onChange={(e) => updateChallenge(index, 'notify_email', e.target.checked)}
                          className="rounded border-gray-600 text-green-500 focus:ring-green-500"
                        />
                        <label htmlFor={`notify-email-${index}`} className="text-xs text-gray-400 flex items-center gap-1">
                          <span className="text-green-400">Email</span>
                        </label>
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">Sends notifications to subscribers when challenge is created</p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 pt-2 border-t border-red-900/20">
          <Button
            type="button"
            variant="outline"
            onClick={addChallenge}
            className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/20"
            data-testid="add-challenge-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Challenge
          </Button>
          
          <Button
            type="button"
            variant="outline"
            onClick={handleSaveDraftManual}
            disabled={isSavingDraft}
            className="border-purple-500/30 text-purple-400 hover:bg-purple-500/20"
            data-testid="save-draft-btn"
          >
            <Save className="w-4 h-4 mr-1" />
            {isSavingDraft ? 'Saving Draft...' : 'Save Draft'}
          </Button>

          <Button
            type="button"
            onClick={handleCreateAll}
            disabled={validCount === 0 || isCreating}
            className="bg-green-600 hover:bg-green-700 flex-1 sm:flex-none"
            data-testid="create-challenges-btn"
          >
            {isCreating ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
            ) : (
              <Zap className="w-4 h-4 mr-2" />
            )}
            Create {validCount} Challenge{validCount !== 1 ? 's' : ''}
          </Button>
        </div>

        {/* Quick Tips */}
        <div className="text-xs text-gray-500 p-2 bg-black/30 rounded-lg">
          <p className="font-bold text-gray-400 mb-1">💡 Draft & Challenge Builder:</p>
          <ul className="list-disc list-inside space-y-0.5">
            <li>Click <strong>Save Draft</strong> to save your work as a draft anytime</li>
            <li>Drafts are saved privately and won't be published until you're ready</li>
            <li>Click <strong>Create Challenge</strong> when ready to launch the challenge live</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};

// Prize Configuration Row Component
const PrizeConfigRow = ({ place, config, currency, onUpdate }) => {
  const placeLabels = { 1: '🥇 1st Place', 2: '🥈 2nd Place', 3: '🥉 3rd Place' };
  const placeColors = { 
    1: 'border-yellow-500/30 bg-yellow-500/5', 
    2: 'border-gray-500/30 bg-gray-500/5', 
    3: 'border-amber-500/30 bg-amber-500/5' 
  };
  
  return (
    <div className={`p-3 rounded-lg border ${placeColors[place]}`}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
        <Label className="text-sm font-bold">{placeLabels[place]}</Label>
        <div className="flex flex-wrap gap-1">
          {PRIZE_TYPES.map(({ value, label, icon: Icon, color }) => (
            <Button
              key={value}
              type="button"
              size="sm"
              variant={config?.prize_type === value ? "default" : "outline"}
              className={`h-7 px-2 text-xs ${
                config?.prize_type === value 
                  ? value === 'money' ? 'bg-green-600 hover:bg-green-700' 
                    : value === 'game_code' ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-purple-600 hover:bg-purple-700'
                  : 'border-gray-700 text-gray-400 hover:bg-gray-800'
              }`}
              onClick={() => onUpdate('prize_type', value)}
              data-testid={`prize-${place}-type-${value}`}
            >
              <Icon className="w-3 h-3 mr-1" />
              {label}
            </Button>
          ))}
        </div>
      </div>
      
      {/* Prize Type Specific Fields */}
      {config?.prize_type === 'money' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div>
            <Label className="text-xs text-gray-400">Amount ({currency})</Label>
            <Input
              type="number"
              value={config.amount || ''}
              onChange={(e) => onUpdate('amount', parseFloat(e.target.value) || 0)}
              className="bg-black/50 border-green-500/30 h-8"
              placeholder="100"
              data-testid={`prize-${place}-amount`}
            />
          </div>
          <div>
            <Label className="text-xs text-gray-400">Description (optional)</Label>
            <Input
              value={config.description || ''}
              onChange={(e) => onUpdate('description', e.target.value)}
              className="bg-black/50 border-gray-700 h-8"
              placeholder="Cash prize via PayPal"
            />
          </div>
        </div>
      )}
      
      {config?.prize_type === 'game_code' && (
        <div className="space-y-2">
          <div>
            <Label className="text-xs text-gray-400">Game/Product Name (public)</Label>
            <Input
              value={config.description || ''}
              onChange={(e) => onUpdate('description', e.target.value)}
              className="bg-black/50 border-blue-500/30 h-8"
              placeholder="Elden Ring Steam Key"
              data-testid={`prize-${place}-description`}
            />
          </div>
          <div>
            <Label className="text-xs text-blue-400 flex items-center gap-1">
              <Key className="w-3 h-3" /> Code (private - only winner sees this)
            </Label>
            <Input
              value={config.code || ''}
              onChange={(e) => onUpdate('code', e.target.value)}
              className="bg-black/50 border-blue-500/30 h-8 font-mono"
              placeholder="XXXX-XXXX-XXXX-XXXX"
              data-testid={`prize-${place}-code`}
            />
          </div>
        </div>
      )}
      
      {config?.prize_type === 'item' && (
        <div className="space-y-2">
          <div>
            <Label className="text-xs text-gray-400">Item Name (public)</Label>
            <Input
              value={config.description || ''}
              onChange={(e) => onUpdate('description', e.target.value)}
              className="bg-black/50 border-purple-500/30 h-8"
              placeholder="Gaming Mouse, Headset, T-Shirt..."
              data-testid={`prize-${place}-description`}
            />
          </div>
          <div>
            <Label className="text-xs text-purple-400 flex items-center gap-1">
              <Package className="w-3 h-3" /> Item Details (private - for your reference)
            </Label>
            <Input
              value={config.item_details || ''}
              onChange={(e) => onUpdate('item_details', e.target.value)}
              className="bg-black/50 border-purple-500/30 h-8"
              placeholder="Logitech G502 Hero, ships from warehouse..."
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Rules Editor Component - Advanced rule management with visibility controls
const RulesEditor = ({ rulesConfig, onChange, testIdPrefix }) => {
  const [newRuleText, setNewRuleText] = useState('');
  const [previewMode, setPreviewMode] = useState('live'); // 'live' or 'scheduled'

  const addRule = () => {
    if (!newRuleText.trim()) return;
    const newRule = {
      text: newRuleText.trim(),
      visibility: RULE_VISIBILITY.ALWAYS
    };
    onChange([...rulesConfig, newRule]);
    setNewRuleText('');
  };

  const updateRule = (index, field, value) => {
    const updated = rulesConfig.map((rule, i) => 
      i === index ? { ...rule, [field]: value } : rule
    );
    onChange(updated);
  };

  const removeRule = (index) => {
    onChange(rulesConfig.filter((_, i) => i !== index));
  };

  const moveRule = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= rulesConfig.length) return;
    
    const updated = [...rulesConfig];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    onChange(updated);
  };

  return (
    <div className="p-3 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-lg space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-xs text-blue-400 font-bold flex items-center gap-2">
          <FileText className="w-4 h-4" />
          Challenge Rules
        </Label>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span className="flex items-center gap-1"><Eye className="w-3 h-3 text-green-400" /> Always</span>
          <span className="flex items-center gap-1"><EyeOff className="w-3 h-3 text-yellow-400" /> Live only</span>
          <span className="flex items-center gap-1"><Lock className="w-3 h-3 text-blue-400" /> Blur</span>
        </div>
      </div>

      {/* Preview Toggle */}
      <div className="flex items-center justify-between p-2 bg-gray-800/50 rounded-lg">
        <span className="text-xs text-gray-400 flex items-center gap-2">
          <Eye className="w-4 h-4" /> Preview Mode:
        </span>
        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => setPreviewMode('live')}
            className={`px-3 py-1 text-xs rounded-lg transition-all ${
              previewMode === 'live'
                ? 'bg-green-500 text-black font-bold'
                : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
            }`}
          >
            🟢 Live
          </button>
          <button
            type="button"
            onClick={() => setPreviewMode('scheduled')}
            className={`px-3 py-1 text-xs rounded-lg transition-all ${
              previewMode === 'scheduled'
                ? 'bg-yellow-500 text-black font-bold'
                : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
            }`}
          >
            ⏰ Scheduled
          </button>
        </div>
      </div>

      {/* Rules Preview */}
      {rulesConfig.length > 0 && (
        <div className="p-3 bg-gray-900/50 rounded-lg border border-gray-700">
          <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">
            Preview: How rules appear when {previewMode === 'live' ? 'LIVE' : 'SCHEDULED'}
          </p>
          <ul className="space-y-1">
            {rulesConfig.map((rule, i) => {
              // Apply visibility rules
              if (previewMode === 'scheduled') {
                if (rule.visibility === 'live_only') return null;
                if (rule.visibility === 'blur_scheduled') {
                  return (
                    <li key={i} className="text-xs text-gray-400 flex items-start gap-2 blur-sm select-none">
                      <span className="text-yellow-400">•</span>
                      <span>{rule.text}</span>
                    </li>
                  );
                }
              }
              return (
                <li key={i} className="text-xs text-gray-300 flex items-start gap-2">
                  <span className="text-green-400">•</span>
                  <span>{rule.text}</span>
                </li>
              );
            })}
            {previewMode === 'scheduled' && rulesConfig.some(r => r.visibility === 'live_only') && (
              <li className="text-[10px] text-yellow-400/60 italic mt-2">
                🔒 {rulesConfig.filter(r => r.visibility === 'live_only').length} rule(s) hidden until live
              </li>
            )}
          </ul>
        </div>
      )}

      {/* Rules List */}
      <div className="space-y-2 max-h-[300px] overflow-y-auto">
        {rulesConfig.length === 0 ? (
          <p className="text-xs text-gray-500 italic text-center py-4">
            No rules added yet. Add rules below.
          </p>
        ) : (
          rulesConfig.map((rule, index) => (
            <div 
              key={index}
              className="flex items-start gap-2 p-2 bg-black/30 border border-gray-800 rounded-lg group"
            >
              {/* Drag handle and number */}
              <div className="flex flex-col items-center gap-1 pt-1">
                <span className="text-xs text-gray-500 font-bold">{index + 1}</span>
                <div className="flex flex-col">
                  <button 
                    onClick={() => moveRule(index, -1)}
                    disabled={index === 0}
                    className="text-gray-600 hover:text-gray-400 disabled:opacity-30"
                  >
                    <ChevronUp className="w-3 h-3" />
                  </button>
                  <button 
                    onClick={() => moveRule(index, 1)}
                    disabled={index === rulesConfig.length - 1}
                    className="text-gray-600 hover:text-gray-400 disabled:opacity-30"
                  >
                    <ChevronDown className="w-3 h-3" />
                  </button>
                </div>
              </div>

              {/* Rule content */}
              <div className="flex-1 space-y-2">
                <Input
                  value={rule.text}
                  onChange={(e) => updateRule(index, 'text', e.target.value)}
                  className="bg-black/50 border-gray-700 h-8 text-sm"
                  placeholder="Enter rule text..."
                  data-testid={`${testIdPrefix}-rule-${index}-text`}
                />
                
                {/* Visibility selector */}
                <div className="flex items-center gap-1">
                  {[
                    { value: RULE_VISIBILITY.ALWAYS, label: 'Always', icon: Eye, color: 'green' },
                    { value: RULE_VISIBILITY.LIVE_ONLY, label: 'Live Only', icon: EyeOff, color: 'yellow' },
                    { value: RULE_VISIBILITY.BLUR_SCHEDULED, label: 'Blur', icon: Lock, color: 'blue' }
                  ].map(({ value, label, icon: Icon, color }) => (
                    <Button
                      key={value}
                      type="button"
                      size="sm"
                      variant={rule.visibility === value ? "default" : "outline"}
                      className={`h-6 px-2 text-[10px] ${
                        rule.visibility === value 
                          ? `bg-${color}-600 hover:bg-${color}-700` 
                          : 'border-gray-700 text-gray-500 hover:bg-gray-800'
                      }`}
                      onClick={() => updateRule(index, 'visibility', value)}
                      data-testid={`${testIdPrefix}-rule-${index}-visibility-${value}`}
                    >
                      <Icon className="w-3 h-3 mr-1" />
                      {label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Delete button */}
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 text-gray-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={() => removeRule(index)}
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          ))
        )}
      </div>

      {/* Add new rule */}
      <div className="flex gap-2 pt-2 border-t border-gray-800">
        <Input
          value={newRuleText}
          onChange={(e) => setNewRuleText(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && addRule()}
          className="bg-black/50 border-gray-700 h-8 text-sm flex-1"
          placeholder="Type a new rule and press Enter..."
          data-testid={`${testIdPrefix}-new-rule-input`}
        />
        <Button
          type="button"
          size="sm"
          onClick={addRule}
          disabled={!newRuleText.trim()}
          className="bg-blue-600 hover:bg-blue-700 h-8"
          data-testid={`${testIdPrefix}-add-rule-btn`}
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>

      {/* Legend/Help */}
      <div className="text-xs text-gray-600 space-y-1 pt-2 border-t border-gray-800">
        <p><Eye className="w-3 h-3 inline text-green-400" /> <strong>Always:</strong> Rule visible on both scheduled & live challenges</p>
        <p><EyeOff className="w-3 h-3 inline text-yellow-400" /> <strong>Live Only:</strong> Rule hidden on scheduled, shown when live</p>
        <p><Lock className="w-3 h-3 inline text-blue-400" /> <strong>Blur:</strong> Rule shown blurred on scheduled, clear when live</p>
      </div>
    </div>
  );
};

export default ChallengeBuilder;
